import React, { useEffect, useMemo, useState } from "react";
import { login, requestPasswordReset, resetPassword } from "../services/authService";
import "./LoginForm.css";
import logoMelonares from "../assets/logo-melonares.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function LoginForm({ onLogin }) {
  // uiMode: "login" | "forgot" | "reset"
  const tokenFromUrl = useMemo(() => new URLSearchParams(window.location.search).get("token"), []);
  const [uiMode, setUiMode] = useState(tokenFromUrl ? "reset" : "login");

  // comunes
  const [error, setError] = useState("");

  // login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // forgot state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // reset state
  const [token, setToken] = useState(tokenFromUrl || "");
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  useEffect(() => {
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setUiMode("reset");
    }
  }, [tokenFromUrl]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      onLogin?.();
    } catch {
      setError("Credenciales incorrectas");
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await requestPasswordReset(forgotEmail);
      setForgotSent(true); // siempre OK (respuesta genérica)
    } catch {
      setError("No se pudo enviar el enlace. Intenta de nuevo.");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setResetMsg("");
    if (!token) { setError("El enlace es inválido o falta el token."); return; }
    if (pwd1.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (pwd1 !== pwd2) { setError("Las contraseñas no coinciden."); return; }
    try {
      await resetPassword(token, pwd1);
      setResetMsg("Contraseña actualizada. Ya puedes iniciar sesión.");
      // Opcional: volver a login tras 2s
      // setTimeout(() => setUiMode("login"), 2000);
    } catch (e) {
      setError(e.message || "No se pudo restablecer la contraseña.");
    }
  };

  // helpers UI
  const goLogin = () => { setUiMode("login"); setError(""); setForgotSent(false); setResetMsg(""); };
  const goForgot = () => { setUiMode("forgot"); setError(""); setForgotSent(false); };
  const goReset = () => { setUiMode("reset"); setError(""); setResetMsg(""); };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* Logo */}
        <div className="logo-full-container">
          <img src={logoMelonares} alt="Logo Los Melonares" className="logo-full-image" />
        </div>

        {/* Título dinámico */}
        <p className="login-prompt">
          {uiMode === "login" && "Iniciar sesión"}
          {uiMode === "forgot" && "Recuperar contraseña"}
          {uiMode === "reset" && "Restablecer contraseña"}
        </p>

        {/* --- LOGIN --- */}
        {uiMode === "login" && (
          <form onSubmit={handleLogin}>
            <div className="input-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo"
                className="input-field"
                required
                autoComplete="username"
              />
            </div>

            <div className="input-group password-field">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                className="input-field"
                required
              />
              {/* Ícono de ojo */}
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>

            {error && <p className="error-message">{error}</p>}

            <div className="forgot-password">
              <button type="button" className="linklike" onClick={goForgot}>
                Olvidé mi contraseña
              </button>
            </div>

            <button type="submit" className="login-button">Entrar</button>
          </form>
        )
        }

        {/* --- FORGOT --- */}
        {
          uiMode === "forgot" && (
            <form onSubmit={handleForgot}>
              {!forgotSent ? (
                <>
                  <div className="input-group">
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Introduce tu correo"
                      className="input-field"
                      required
                    />
                  </div>
                  {error && <p className="error-message">{error}</p>}
                  <button type="submit" className="login-button">Enviar enlace</button>

                  <div className="forgot-password" style={{ marginTop: 12 }}>
                    <button type="button" className="linklike" onClick={goLogin}>Volver a iniciar sesión</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="info-message">
                    Si el correo existe, te hemos enviado un enlace para restablecer la contraseña.
                  </p>
                  <div className="actions-row">
                    <button type="button" className="login-button" onClick={goLogin}>Volver al inicio de sesión</button>
                    <button type="button" className="secondary-button" onClick={goReset}>
                      Ya tengo el enlace
                    </button>
                  </div>
                </>
              )}
            </form>
          )
        }

        {/* --- RESET --- */}
        {
          uiMode === "reset" && (
            <form onSubmit={handleReset}>
              {/* Si el token vino por URL ya lo tenemos; si no, campo para pegarlo */}
              {!tokenFromUrl && (
                <div className="input-group">
                  <input
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Pega aquí el token del correo"
                    className="input-field"
                    required
                  />
                </div>
              )}

              <div className="input-group">
                <input
                  type="password"
                  value={pwd1}
                  onChange={(e) => setPwd1(e.target.value)}
                  placeholder="Nueva contraseña"
                  className="input-field"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="input-group">
                <input
                  type="password"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="input-field"
                  required
                  autoComplete="new-password"
                />
              </div>

              {error && <p className="error-message">{error}</p>}
              {resetMsg && <p className="success-message">{resetMsg}</p>}

              <button type="submit" className="login-button">Guardar contraseña</button>

              <div className="forgot-password" style={{ marginTop: 12 }}>
                <button type="button" className="linklike" onClick={goLogin}>Volver a iniciar sesión</button>
              </div>
            </form>
          )
        }

        {/* Pie (opcional) */}
        {/* <div className="footer-links">
          <a href="#">Términos de Uso</a>
          <a href="#">Política de privacidad</a>
          <a href="#">Aviso Legal</a>
        </div> */}
      </div >
    </div >
  );
}
